import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";

/**
 * Iter XX.46 — Earn Hub.
 * ======================
 * Non-partners visiting `/earn` (or `/partner`) land here.
 *
 * The hero personalises to what the visitor already tells us they are:
 *   1. `?as=coach|academy|creator|…` on the URL takes precedence.
 *   2. `gully_earn_persona_hint` in localStorage (last thing they picked).
 *   3. Default → `player` framing — the broadest audience for Gully.
 *
 * Zero backend calls. All personalisation is local so this page loads
 * instantly on mobile.
 */

const PERSONAS = {
  player:     { key: "player",     label: "Player",     headline: "Invite your teammates.",   subtitle: "Help them discover Gully. Earn whenever they become Gully Plus members.", cta: "Get my link" },
  coach:      { key: "coach",      label: "Coach",      headline: "Invite your players.",     subtitle: "Give every player their own AI cricket companion. Earn as Gully grows.",  cta: "Start earning" },
  academy:    { key: "academy",    label: "Academy",    headline: "Grow your academy.",       subtitle: "Help every player improve. Track engagement. Earn as your academy grows with Gully.", cta: "Grow my academy" },
  creator:    { key: "creator",    label: "Creator",    headline: "Tell your followers.",     subtitle: "Share Gully. Track your referrals. Earn as your audience grows.",       cta: "Share with my audience" },
  influencer: { key: "influencer", label: "Influencer", headline: "Turn your audience into a business.", subtitle: "Track referral revenue in real time. Grounded numbers, never fabricated.", cta: "Start earning" },
  club:       { key: "club",       label: "Club",       headline: "Grow your club.",          subtitle: "Bring your members onto Gully. Broadcast, track engagement, earn as your club grows.", cta: "Invite my members" },
  school:     { key: "school",     label: "School",     headline: "Empower your students.",   subtitle: "Give every student a coach in their pocket. Track squad-wide engagement. Earn as your programme grows.", cta: "Invite my students" },
  league:     { key: "league",     label: "League",     headline: "Build your cricket community.", subtitle: "Rally your tournament participants around Gully. Share updates, broadcast fixtures, earn as your community grows.", cta: "Grow my community" },
};

function resolvePersona() {
  try {
    const qs = new URLSearchParams(window.location.search);
    const as = (qs.get("as") || "").toLowerCase();
    if (PERSONAS[as]) return as;
    const stored = (localStorage.getItem("gully_earn_persona_hint") || "").toLowerCase();
    if (PERSONAS[stored]) return stored;
  } catch { /* ignore */ }
  return "player";
}

export default function EarnHub() {
  const [persona, setPersona] = useState("player");

  useEffect(() => {
    setPersona(resolvePersona());
  }, []);

  function pick(p) {
    setPersona(p);
    try { localStorage.setItem("gully_earn_persona_hint", p); } catch { /* ignore */ }
  }

  const h = PERSONAS[persona] || PERSONAS.player;
  // Non-player personas go through the full claim wizard.
  // Players don't have a partner page yet — steer them to claim as
  // "creator" (creators/influencers is the closest fit; the claim
  // wizard lets them change).
  const claimHref = persona === "player"
    ? `/partner/claim?as=creator`
    : `/partner/claim?as=${persona}`;

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 flex flex-col" data-testid="earn-hub" data-persona={persona}>
      <div className="flex-1 max-w-3xl mx-auto px-6 py-10 sm:py-14 w-full">
        <Link to="/me" className="text-sm text-neutral-500 hover:text-neutral-300" data-testid="earn-hub-back">← Back</Link>

        <div className="text-xs uppercase tracking-widest text-emerald-400/80 mt-8 mb-3">Help other cricketers · Earn as Gully grows</div>
        <h1 className="text-4xl sm:text-5xl font-semibold leading-tight mb-4" data-testid="earn-hero-headline">
          {h.headline}
        </h1>
        <p className="text-neutral-300 text-lg leading-relaxed max-w-2xl" data-testid="earn-hero-subtitle">
          {h.subtitle}
        </p>

        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          <Link
            to={claimHref}
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-medium text-base"
            data-testid="earn-hero-cta"
          >
            {h.cta} →
          </Link>
          <Link
            to="/@iter43_smoke"
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl border border-neutral-800 hover:border-neutral-700 text-neutral-200 font-medium text-base"
            data-testid="earn-hero-example"
          >
            See an example page
          </Link>
        </div>

        {/* Persona picker — lets visitors change the hero explicitly. */}
        <div className="mt-10" data-testid="earn-persona-picker">
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-3">I am a…</div>
          <div className="flex flex-wrap gap-2">
            {Object.values(PERSONAS).map(p => (
              <button
                key={p.key}
                onClick={() => pick(p.key)}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                  persona === p.key
                    ? "bg-emerald-950/60 border-emerald-600 text-emerald-200"
                    : "border-neutral-800 text-neutral-400 hover:border-neutral-700 hover:text-neutral-200"
                }`}
                data-testid={`earn-persona-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Benefits — same real features, framed for the current persona. */}
        <div className="mt-14 grid gap-4 sm:grid-cols-2" data-testid="earn-benefits">
          <Benefit title="Your permanent page">
            A public page at askgully.com/@yourhandle. Add it to any bio and the referrals compound.
          </Benefit>
          <Benefit title="One-click sharing pack">
            Referral link, Instagram bio snippet, WhatsApp message, story copy, and a downloadable QR — pre-drafted.
          </Benefit>
          <Benefit title="Marketing Studio">
            Warm, grounded drafts for captions, IG carousels, reel scripts, WhatsApp broadcasts and emails. You always stay in control.
          </Benefit>
          <Benefit title="Wallet + attribution">
            Every player you bring is tracked. When they upgrade to Gully Plus, your share lands in a wallet you can cash out.
          </Benefit>
          <Benefit title="Team Pulse">
            For academies, clubs, schools, leagues — anonymised, K-anonymity protected roster health. Never player-private.
          </Benefit>
          <Benefit title="Growth Coach">
            Deterministic weekly nudges grounded in your own numbers. Never compared to other partners.
          </Benefit>
        </div>

        <div className="mt-14 text-sm text-neutral-500 leading-relaxed max-w-2xl" data-testid="earn-safety">
          Gully never sees your players&apos; private content. Every insight surfaced is either about your public
          page or aggregated across at least 5 members. No fee to join, no lock-in. Read our{" "}
          <Link to="/trust" className="text-emerald-400 hover:text-emerald-300 underline decoration-dotted">Trust Center</Link>.
        </div>

        <div className="mt-10 flex flex-col sm:flex-row gap-3 items-center justify-between border-t border-neutral-900 pt-6">
          <div className="text-sm text-neutral-400">Takes about two minutes.</div>
          <Button asChild className="bg-emerald-500 hover:bg-emerald-400 text-neutral-950">
            <Link to={claimHref} data-testid="earn-hero-cta-bottom">{h.cta} →</Link>
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Benefit({ title, children }) {
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-5">
      <div className="text-sm font-medium text-emerald-300 mb-1.5">{title}</div>
      <div className="text-sm text-neutral-400 leading-relaxed">{children}</div>
    </div>
  );
}
